import { useCallback, useState } from 'react';
import type { Video } from '@blackblack/shared';
import { api } from '../../lib/api';
import { useInfinite } from '../../lib/query';
import { Link, navigate } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { usePageActive } from '../../app/Outlet';
import { Icon } from '../../components/ui/Icon';
import { Button, EmptyState, cx } from '../../components/ui/primitives';
import { LogoMark } from '../../components/ui/Logo';
import { VerticalFeed } from './VerticalFeed';
import styles from './feed.module.css';

type Tab = 'foryou' | 'following';

/** Home: the endless vertical video feed with "Voor jou" / "Volgend" tabs. */
export function FeedPage() {
  const [tab, setTab] = useState<Tab>('foryou');
  const { status, me, requireAuth } = useAuth();
  const pageActive = usePageActive();
  const authKey = status === 'loading' ? null : me?.id ?? 'anon';

  const forYou = useInfinite<Video & { _key: string }>(authKey && `feed:foryou:${authKey}`, async (cursor) => {
    const page = await api.page<Video>('/feed', { cursor, limit: 6 });
    // The For You feed is endless and may revisit videos in a new round → unique keys.
    const round = cursor ? cursor.length : 0;
    return { ...page, items: page.items.map((v, i) => ({ ...v, _key: `${v.id}:${round}:${i}:${cursor ?? ''}` })) };
  });
  const following = useInfinite<Video>(tab === 'following' && status === 'authed' ? `feed:following:${me?.id}` : null, (cursor) => api.page<Video>('/feed', { tab: 'following', cursor, limit: 6 }));

  const loadMoreForYou = useCallback(() => forYou.loadMore(), [forYou]);
  const loadMoreFollowing = useCallback(() => following.loadMore(), [following]);

  const selectTab = (t: Tab) => {
    if (t === 'following' && !requireAuth('Log in om video’s te zien van accounts die je volgt')) return;
    setTab(t);
  };

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <span className={styles.brand} aria-hidden="true">
          <LogoMark size={22} />
        </span>
        <nav className={styles.feedTabs} aria-label="Feed">
          <button className={cx(styles.feedTab, tab === 'following' && styles.feedTabActive)} onClick={() => selectTab('following')}>
            Volgend
          </button>
          <button className={cx(styles.feedTab, tab === 'foryou' && styles.feedTabActive)} onClick={() => selectTab('foryou')}>
            Voor jou
          </button>
        </nav>
        <div className={styles.topActions}>
          <Link to="/search" className={styles.topIcon} aria-label="Zoeken">
            <Icon name="search" size={22} />
          </Link>
          {status === 'authed' && (
            <Link to="/notifications" className={styles.topIcon} aria-label="Meldingen">
              <Icon name="bell" size={22} />
              {(me?.unreadNotifications ?? 0) > 0 && <span className={styles.dot} />}
            </Link>
          )}
        </div>
      </header>

      <div hidden={tab !== 'foryou'} className={styles.feedHost}>
        <VerticalFeed
          items={forYou.items}
          hasMore={forYou.hasMore}
          loading={forYou.loading || !authKey}
          onNearEnd={loadMoreForYou}
          active={pageActive && tab === 'foryou'}
          empty={
            forYou.error ? (
              <EmptyState icon="alert" title="Feed kon niet laden" text="Controleer je verbinding." action={<Button variant="secondary" icon="refresh" onClick={() => void forYou.refresh()}>Opnieuw</Button>} />
            ) : (
              <EmptyState icon="sparkle" title="Nog geen video's" text="Wees de eerste die iets plaatst!" action={<Button icon="plus" onClick={() => navigate('/create')}>Video maken</Button>} />
            )
          }
        />
      </div>
      {tab === 'following' && (
        <div className={styles.feedHost}>
          <VerticalFeed
            items={following.items}
            hasMore={following.hasMore}
            loading={following.loading}
            onNearEnd={loadMoreFollowing}
            active={pageActive}
            empty={<EmptyState icon="users" title="Je bent helemaal bij" text="Volg creators om hun nieuwe video's hier te zien." action={<Button onClick={() => navigate('/discover')}>Creators ontdekken</Button>} />}
          />
        </div>
      )}
    </div>
  );
}
